import { memo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography } from "@mui/material";
import { EventBusy, PlayArrow } from "@mui/icons-material";
import dayjs from "dayjs";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import type { BookingInviteStatus } from "../helpers/bookingInviteStatus";
import type { AffiliateTeleconsultoriaRow } from "../services/affiliateTeleconsultoriasService";

const STATUS_COLOR: Record<BookingInviteStatus, "success" | "warning" | "error" | "default"> = {
  accepted: "success",
  pending: "warning",
  rejected: "error",
  expired: "default",
  cancelled: "default",
};
/** Convite que não vai mais acontecer: a reserva pode ser cancelada (libera o horário). */
const CANCELLABLE_STATUSES = new Set<BookingInviteStatus>(["rejected", "expired", "cancelled"]);

interface AffiliateBookedRowProps {
  row: AffiliateTeleconsultoriaRow;
  isHost: boolean;
  isCancelling: boolean;
  /** Recém-marcada: borda em destaque por alguns segundos. */
  isHighlighted?: boolean;
  onCancel: (row: AffiliateTeleconsultoriaRow) => void;
}

/** Linha da marcada: status do convite, iniciar (solicitante) ou cancelar reserva. */
export const AffiliateBookedRow = memo(function AffiliateBookedRow({
  row,
  isHost,
  isCancelling,
  isHighlighted = false,
  onCancel,
}: AffiliateBookedRowProps) {
  const { verifyEncounter } = useClinicalRegisterContext();
  const [isStarting, setIsStarting] = useState(false);
  const start = row.start ? dayjs(row.start) : undefined;
  const canCancel = CANCELLABLE_STATUSES.has(row.invite.status);

  const handleStart = async () => {
    setIsStarting(true);
    try {
      await verifyEncounter(row.appointment);
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      role="listitem"
      sx={{
        p: 1.5,
        transition: "border-color 0.3s, box-shadow 0.3s",
        ...(isHighlighted && {
          borderColor: "primary.main",
          boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
        }),
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 2 }} alignItems={{ sm: "center" }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ minWidth: 64, textAlign: "center", flexShrink: 0 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {start?.isValid() ? start.format("HH:mm") : "--:--"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {start?.isValid() ? start.format("DD/MM") : "Sem data"}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {row.patientName}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Teleconsultor: {row.teleconsultorName} · Solicitante: {row.requesterName}
            </Typography>
            <Chip
              size="small"
              label={row.invite.label}
              color={STATUS_COLOR[row.invite.status]}
              variant={row.invite.status === "accepted" ? "filled" : "outlined"}
              sx={{ alignSelf: "flex-start" }}
            />
          </Stack>
        </Stack>
        {canCancel ? (
          <Button
            variant="outlined"
            color="error"
            startIcon={isCancelling ? <CircularProgress size={16} color="inherit" /> : <EventBusy />}
            disabled={isCancelling}
            onClick={() => onCancel(row)}
            sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
          >
            Cancelar reserva
          </Button>
        ) : (
          isHost && (
            <Button
              variant="contained"
              startIcon={isStarting ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
              disabled={isStarting}
              onClick={() => void handleStart()}
              sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
            >
              Iniciar atendimento
            </Button>
          )
        )}
      </Stack>
    </Paper>
  );
});
